import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { BadgeCheckIcon, ChevronDownIcon } from 'lucide-react';
import type { CensusRecord } from '../types/census';
type CensusRowProps = {
  record: CensusRecord;
};
type FieldProps = {
  label: string;
  value: string;
  mono?: boolean;
};
export const CENSUS_GRID = 'lg:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.6fr)_minmax(0,1.7fr)_auto]';

/** Table cell on desktop, "Label: value" line on mobile */
function Field({
  label,
  value,
  mono = false
}: FieldProps) {
  return <span className={`min-w-0 text-foreground/70 lg:truncate ${mono ? 'tabular-nums' : ''}`} title={value || undefined} data-id="element-135" data-mp="3bkj7legteyti">
      <span className="font-semibold text-foreground/50 lg:hidden" data-id="element-136" data-mp="3gk5l7p0k2h2u">{label}: </span>
      {value || '—'}
    </span>;
}
function Email({
  email
}: {
  email: string;
}) {
  return <span className="min-w-0 text-foreground/70" data-id="element-137" data-mp="vtdhtaq054xe">
      <span className="font-semibold text-foreground/50 lg:hidden" data-id="element-138" data-mp="vs2cv7ktlr4p">Correo: </span>
      {email ? <a href={`mailto:${email}`} className="break-all text-primary underline-offset-2 hover:underline lg:block lg:truncate lg:break-normal" title={email} data-id="element-139" data-mp="2jroc9igkhnil">
          {email}
        </a> : '—'}
    </span>;
}
function StatusBadge() {
  return <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 lg:w-28" data-id="element-140" data-mp="25sooed0vpcnh">
      <BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-141" data-mp="1w3n4ibr7qb48" /> Habilitada
    </span>;
}
export function CensusRow({
  record: r
}: CensusRowProps) {
  const [open, setOpen] = useState(false);
  const detailsId = `census-details-${r.id}`;
  return <li className="border-t border-primary/5 text-sm" data-id="element-142" data-mp="1n403hr2eb7bp">
      {/* Mobile / tablet: collapsible */}
      <div className="lg:hidden" data-id="element-143" data-mp="1i2aq44rpahkg">
        <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls={detailsId} className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-primary/[0.03] focus:outline-none focus-visible:bg-primary/5" data-id="element-144" data-mp="1i5orb9omokqb">
          <span className="min-w-0 flex-1" data-id="element-145" data-mp="tiqxjzjy2w76">
            <span className="block font-bold leading-snug text-primary" data-id="element-146" data-mp="tk0weqymg8ev">{r.businessName}</span>
            <span className="mt-0.5 block text-xs text-foreground/55" data-id="element-147" data-mp="yivgrxc8m513">{r.personType || 'Tipo de persona no registrado'}</span>
          </span>
          <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary/60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-148" data-mp="1m9r0s8agbvxs" />
        </button>
        <AnimatePresence initial={false} data-id="element-149" data-mp="1ch210ao9k6ia">
          {open && <motion.div id={detailsId} initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: 'easeOut'
        }} className="overflow-hidden" data-id="element-150" data-mp="1hfwjfbibjt1v">
              <div className="mx-5 mb-4 grid gap-1.5 rounded-xl bg-[#F4F7FC] p-4" data-id="element-151" data-mp="3ulthh8ihsa5q">
                <Field label="ID" value={r.id} mono data-id="element-152" data-mp="3pn9wpbfmx4ey" />
                <Field label="NIT" value={r.nit} mono data-id="element-153" data-mp="87tmkwytfrfe" />
                <Field label="Matrícula" value={r.registration} mono data-id="element-154" data-mp="86ia88rb1bco" />
                <Field label="Representante" value={r.legalRep} data-id="element-155" data-mp="38goth6eozy1" />
                <Email email={r.email} data-id="element-156" data-mp="3b2xjk519lhu" />
                <span className="mt-1.5" data-id="element-157" data-mp="3bnqq4n2h214r">
                  <StatusBadge data-id="element-158" data-mp="3bqmlc3mxqbpi" />
                </span>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Desktop: table row */}
      <div className={`hidden gap-4 px-5 py-3.5 lg:grid lg:items-center ${CENSUS_GRID}`} data-id="element-159" data-mp="3kqrtifzyde4q">
        <span className="min-w-0" data-id="element-160" data-mp="3gmtue5amo3bo">
          <span className="block truncate font-bold text-primary" title={r.businessName} data-id="element-161" data-mp="3gpzjvln6577x">
            {r.businessName}
          </span>
          <span className="mt-0.5 block text-xs text-foreground/50" data-id="element-162" data-mp="vxxh8eu10gu9">
            ID {r.id}
            {r.personType && ` · ${r.personType}`}
          </span>
        </span>
        <Field label="NIT" value={r.nit} mono data-id="element-163" data-mp="1v960j6982z1a" />
        <Field label="Matrícula" value={r.registration} mono data-id="element-164" data-mp="1v9xjnhlmv4ao" />
        <Field label="Representante" value={r.legalRep} data-id="element-165" data-mp="208il1kcmfu0p" />
        <Email email={r.email} data-id="element-166" data-mp="206xs1z5ygj4y" />
        <StatusBadge data-id="element-167" data-mp="257bxd9g69eza" />
      </div>
    </li>;
}